import { createStore, useStore } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { sircapApi } from "shared/api/sircap";
import { EntityType } from "static/types";
import {
  DisableNotificationsDto,
  EnableNotificationsDto,
} from "shared/api/sircap/Api";
import { accountStore } from "entities/account";
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { requestForToken } from "app/libs/firebase";

export enum UserVerifyStatus {
  None = "none",
  Pending = "pending",
  Success = "success",
  Rejected = "rejected",
}

export interface IUser {
  id: number;
  phone: string;
  requestedPhone: string;
  email: string;
  requestedEmail: string;
  personalAddress: string;
  avatar: string;

  yurName: string;
  companyName: string;
  documents: any;
  additionalDocument: any;

  profileInfo: any;
  firstName: string;
  lastName: string;

  identificator: string;
  tariff: number;

  status: number;
  signContract: boolean;
  sign_date: string | null;

  language: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  hasPinCode: boolean;

  registered: string | null;
}

type User = null | IUser;

type SessionState = {
  user: User;
  accessToken: string;
  refreshToken: string;
  biometric: any | null;
  biometrics: any[];
  biometricOptions: any;
  identificator: string;
  link: string;
  entity: EntityType;
  notificationToken: string | null;
  subAccountSubNumber: string | null;
  setSubAccountSubNumber: (subAccount: string | null) => void;
  requestNotificationToken: () => void;
  enableNotifications: (dto: EnableNotificationsDto) => void;
  disableNotifications: (dto: DisableNotificationsDto) => void;

  crsData: Array<{
    updatedAt: string;
    year: number;
    data: Array<{ country: string; tin: string; reason: string }>;
  }> | null;

  setLink: (link: string) => void;
  addUser: (
    user: User,
    token: string,
    refresh_token: string,
    entityType: EntityType,
    biometrics: any[],
    biometric?: string | null,
    fcmToken?: string | null,
    crsData?: Array<{
      year: number;
      data: Array<{ country: string; tin: string; reason: string }>;
    }> | null,
    subAccounts?: any[] | null
  ) => void;
  updateUserAvatar: (avatar: string) => void;
  updateUser: (dto: any) => void;
  updateUserSignContract: (is: boolean) => void;
  updateCrsData: (
    crsData: Array<{
      year: number;
      data: Array<{ country: string; tin: string; reason: string }>;
    }>
  ) => void;
  logout: () => Promise<void>;

  signContractModal: boolean;
  setSignContractModal: (show: boolean) => void;

  inactiveAccountModal: boolean;
  setInactiveAccountModal: (show: boolean) => void;

  inactiveEmailModal: boolean;
  setInactiveEmailModal: (show: boolean) => void;

  inactivePinCodeModal: boolean;
  setInactivePinCodeModal: (show: boolean) => void;

  notVerifiedModal: boolean;
  setNotVerifiedModal: (show: boolean) => void;

  reachedLimitModal: boolean;
  setReachedLimitModal: (show: boolean) => void;

  crsDataModal: boolean;
  setCrsDataModal: (show: boolean) => void;

  cantTradeModal: boolean;
  setCantTradeModal: (show: boolean) => void;

  holidayNotificationSeenByDate: Record<string, boolean>;
  holidayNotificationLocale: string;
  markHolidayNotificationSeen: (date: string) => void;
  setHolidayNotificationLocale: (locale: string) => void;
};

export const sessionStore = createStore<SessionState>()(
  persist(
    devtools<SessionState>(
      (set: any, get: any) => ({
        user: null,
        accessToken: "",
        refreshToken: "",
        link: "",
        notificationToken: null,
        subAccountSubNumber: null,
        setSubAccountSubNumber: (subAccount: string | null) => {
          set({ subAccountSubNumber: subAccount });
        },
        identificator: "",
        entity: EntityType.Individual,
        biometric: null,
        biometrics: [],
        biometricOptions: null,
        crsData: null,
        requestNotificationToken: async () => {
          try {
            const token = await requestForToken();
            if (token) {
              get().enableNotifications({
                notification_token: token,
                platform: Capacitor.getPlatform(),
              });
            } else {
              // check old token, and disable notifications for old token
            }
          } catch (error) {
            console.log(error);
          }
        },
        enableNotifications: async (dto: EnableNotificationsDto) => {
          try {
            const resp = await sircapApi.nots.enable(dto);
            set({ notificationToken: dto.notification_token });
          } catch (error) {}
        },
        disableNotifications: async (dto: DisableNotificationsDto) => {
          try {
            const resp = await sircapApi.nots.disable(dto);
            set({ notificationToken: null });
          } catch (error) {}
        },

        setLink: (link: string) => {
          set({ link });
        },
        addUser: (
          user: User,
          token: string,
          refresh_token: string,
          entity: EntityType,
          biometrics: any[],
          biometric?: string | null,
          fcmToken?: string | null,
          crsData?: any[] | null,
          subAccounts?: any[] | null
        ) => {
          sircapApi.setSecurityData(token);
          let biometricID = biometric || null;
          if (!biometricID) {
            biometricID = get().biometric || null;
            if (biometricID && !biometrics.includes(biometricID)) {
              biometricID = null;
              const platform = Capacitor.getPlatform();
              if (platform === "ios" || platform === "android") {
                NativeBiometric.deleteCredentials({
                  server: window.location.hostname,
                })
                  .then(() => {})
                  .catch(() => {});
              }
            }
          }

          if (subAccounts) {
            accountStore.setState({ subAccounts });
          }

          set({
            user,
            accessToken: token,
            refreshToken: refresh_token,
            entity,
            identificator: user?.identificator || "",
            notificationToken: fcmToken || null,
            biometrics,
            biometric: biometricID,
            crsData,
          });
        },
        updateUserAvatar: (avatar: string) => {
          set({
            user: {
              ...get().user,
              avatar: avatar ? avatar + "?t=" + new Date().getTime() : "",
            },
          });
        },
        updateUser: (dto: any) => {
          set({
            // @ts-ignore
            user: {
              ...get().user,
              ...dto,
            },
          });
        },
        updateUserSignContract: (is: boolean) => {
          set({
            user: {
              ...get().user,
              signContract: is,
            },
          });
        },
        updateCrsData: (crsData: any[]) => {
          set({ crsData });
        },
        logout: async () => {
          localStorage.removeItem("login");
          await sircapApi.auth.logout({ platform: Capacitor.getPlatform() });
          set({
            user: null,
            accessToken: "",
            refreshToken: "",
            link: "",
            entity: EntityType.Individual,
          });
          sircapApi.setSecurityData(null);
        },

        signContractModal: false,
        setSignContractModal: (show: boolean) => {
          set({ signContractModal: show });
        },

        inactiveAccountModal: false,
        setInactiveAccountModal: (show: boolean) => {
          set({ inactiveAccountModal: show });
        },

        inactiveEmailModal: false,
        setInactiveEmailModal: (show: boolean) => {
          set({ inactiveEmailModal: show });
        },

        inactivePinCodeModal: false,
        setInactivePinCodeModal: (show: boolean) => {
          set({ inactivePinCodeModal: show });
        },

        notVerifiedModal: false,
        setNotVerifiedModal: (show: boolean) => {
          set({ notVerifiedModal: show });
        },

        reachedLimitModal: false,
        setReachedLimitModal: (show: boolean) => {
          set({ reachedLimitModal: show });
        },

        crsDataModal: false,
        setCrsDataModal: (show: boolean) => {
          set({ crsDataModal: show });
        },

        cantTradeModal: false,
        setCantTradeModal: (show: boolean) => {
          set({ cantTradeModal: show });
        },

        holidayNotificationSeenByDate: {},
        holidayNotificationLocale: "en",
        markHolidayNotificationSeen: (date: string) => {
          const prev = get().holidayNotificationSeenByDate || {};
          set({
            holidayNotificationSeenByDate: {
              ...prev,
              [date]: true,
            },
          });
        },
        setHolidayNotificationLocale: (locale: string) => {
          set({ holidayNotificationLocale: locale });
        },
      }),
      {
        name: "Session Store",
      }
    ),
    {
      name: "session",
      version: 1,
      merge: (persistedState, currState) => {
        const {
          user,
          accessToken,
          refreshToken,
          entity,
          notificationToken,
          biometric,
          identificator,
          biometrics,
          subAccountSubNumber,
          holidayNotificationSeenByDate,
          holidayNotificationLocale,
        } = persistedState as Partial<SessionState>;
        if (accessToken) {
          sircapApi.setSecurityData(accessToken);
        }

        return {
          ...currState,
          // user: user || null,
          accessToken: accessToken || "",
          refreshToken: refreshToken || "",
          notificationToken: notificationToken || "",
          biometric: biometric || null,
          identificator: identificator || "",
          biometrics: biometrics || [],
          subAccountSubNumber: subAccountSubNumber || null,
          holidayNotificationSeenByDate: holidayNotificationSeenByDate || {},
          holidayNotificationLocale: holidayNotificationLocale || "en",
        };
      },
    }
  )
);

export const useAuth = () =>
  useStore(sessionStore, (state) => !!state.accessToken);

export const useCurrentUser = () =>
  useStore(sessionStore, (state) => state.user);

export const useUserName = (takeDirectorNameForEntity = false) =>
  useStore(sessionStore, (state) =>
    state.entity === EntityType.Individual
      ? `${state.user?.lastName || ""} ${state.user?.firstName || ""}`
      : takeDirectorNameForEntity
      ? accountStore.getState().kyb?.yur_name || ""
      : state.user?.companyName
  );

export const useUserContract = () =>
  useStore(sessionStore, (state) => state.user?.identificator || "");

export const useUserIsActive = () =>
  useStore(sessionStore, (state) => {
    if (state.user && state.user.status) {
      return state.user.status === 1;
    } else {
      return false;
    }
  });
